import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Paper,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CardMedia,
  TextField,
  FormHelperText,
  Alert,
  Snackbar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { submitReturningUser } from '../lib/cloudSqlClient';

interface FormData {
  track: string;
  activityType: string;
  segment: string;
  userType: string;
  trainingMode: string;
  email: string;
  coachPreference: string;
}

// Hardcoded URL mapping (Season 15 - Summer)
const URL_MAPPING: Record<string, string> = {
  // Summer Track (July-Nov)
  'summer-Coach Guided-Running-new-Std': 'https://www.finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a01coach-guided-run-new-std',
  'summer-Coach Guided-Running-new-Exp': 'https://www.finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a02coach-guided-run-new-exp',
  'summer-Coach Guided-Running-new-Speed': 'https://www.finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a03coach-guided-run-new-speed',
  'summer-Coach Guided-Running-return-Std': 'https://www.finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a04coach-guided-run-return-std',
  'summer-Coach Guided-Running-return-Exp': 'https://www.finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a05coach-guided-run-return-exp',
  'summer-Coach Guided-Running-return-Speed': 'https://www.finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a06coach-guided-run-return-speed',
  'summer-Coach Guided-Walking-new-Std': 'https://www.finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a07coach-guided-walk-new-std',
  'summer-Coach Guided-Walking-return-Std': 'https://finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a08coach-guided-walk-return-std',
  'summer-Self Serve-Running-return-Std': 'https://www.finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a11self-serve-run-return-std',
  'summer-Self Serve-Running-return-Exp': 'https://finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a12self-serve-run-return-exp',
  'summer-Self Serve-Walking-return-Std': 'https://www.finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a14self-serve-walk-return-std',
  'summer-Coach Guided-Masters-new-Std': 'https://www.finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a15masters-walk-new-std',
  'summer-Coach Guided-Masters-return-Std': 'https://www.finalsurge.com/coach/rhwb/training/rhwb-s16admissions/a16masters-walk-return-std',
};

const ProgramForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    track: 'summer',
    activityType: '',
    segment: '',
    userType: '',
    trainingMode: '',
    email: '',
    coachPreference: ''
  });

  const [expanded, setExpanded] = useState<string | false>('program-info');

  // Add state for handling submission status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Add state for email validation
  const [emailError, setEmailError] = useState<string | null>(null);

  const [orientationConfirmed, setOrientationConfirmed] = useState(false);

  useEffect(() => {
    if (formData.userType !== 'new') {
      setOrientationConfirmed(false);
    }
  }, [formData.userType]);

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Separate handler for Select components
  const handleSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;

    if (name === "activityType") {
      if (value === "Masters") {
        setFormData(prev => ({
          ...prev,
          activityType: value,
          segment: "Coach Guided",
          trainingMode: "Std"
        }));
      } else if (value === "Walking") {
        setFormData(prev => ({
          ...prev,
          activityType: value,
          trainingMode: "Std"
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          activityType: value,
          segment: "",
          trainingMode: ""
        }));
      }
    } else if (name === "segment") {
      // If Self Serve is selected, automatically set userType to return
      if (value === "Self Serve") {
        setFormData(prev => ({
          ...prev,
          segment: value,
          userType: "return",
          coachPreference: ""
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          segment: value
        }));
      }
    } else if (name === "userType") {
      setFormData(prev => {
        const next = { ...prev, userType: value };
        if (
          prev.activityType === "Running" &&
          prev.segment === "Coach Guided" &&
          value === "new" &&
          prev.trainingMode === "Speed"
        ) {
          next.trainingMode = "";
        }
        return next;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  // Separate handler for TextField components
  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    
    if (name === 'email' && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError(null);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getTrainingModeOptions = () => {
    if (formData.activityType === 'Walking') return ['Std'];
    if (formData.segment === 'Coach Guided') {
      const isRunningCoachGuidedNew =
        formData.activityType === 'Running' && formData.userType === 'new';
      if (isRunningCoachGuidedNew) return ['Std', 'Exp'];
      return ['Std', 'Exp', 'Speed'];
    }
    return ['Std', 'Exp'];
  };

  const calculateProgramCost = () => {
    if (formData.activityType === 'Running') {
      if (formData.segment === 'Coach Guided') {
        return formData.userType === 'new' ? 70 : 45;
      }
      // Self Serve (UI always sets userType to return for this segment)
      return 30;
    }
    if (formData.activityType === 'Walking') {
      if (formData.segment === 'Coach Guided') {
        return formData.userType === 'new' ? 70 : 45;
      }
      return 25;
    }
    return 0; // Masters or incomplete selection
  };

  const saveReturningUserData = async () => {
    if (formData.userType !== 'return') return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      await submitReturningUser({
        email: formData.email,
        coach_preference: formData.segment === 'Self Serve' ? '' : formData.coachPreference,
        track: formData.track,
        activity_type: formData.activityType,
        segment: formData.segment,
        training_mode: formData.trainingMode,
      });

      setSubmitSuccess(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while saving data.';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
  if (formData.userType === 'new' && !orientationConfirmed) {
    setSubmitError('Please confirm that as a new runner to RHWB you have watched and understood the full orientation video.');
    return;
  }

  const key = `${formData.track}-${formData.segment}-${formData.activityType}-${formData.userType}-${formData.trainingMode}`;
  const url = URL_MAPPING[key];

  if (!url) {
    setSubmitError('No matching program URL found for the selected options.');
    return;
  }

  // Open the URL immediately in response to user click to avoid popup blockers
  // Safari requires popups to be opened synchronously in response to user action
  const newWindow = window.open(url, '_blank');
  
  // If popup was blocked, show error
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    setSubmitError('Popup was blocked. Please allow popups for this site and try again.');
    return;
  }

  // Save data in the background (don't await to avoid blocking)
  if (formData.userType === 'return') {
    saveReturningUserData().catch((error) => {
      // Silently handle errors - don't interrupt user flow
      // The popup is already open, so we don't want to show an error
    });
  }
};
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  return (
    <Container maxWidth="sm">
      <Accordion 
        expanded={expanded === 'program-info'} 
        onChange={handleAccordionChange('program-info')}
        sx={{ mb: 3 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="program-info-content"
          id="program-info-header"
        >
          <Typography variant="h6">Welcome to Season 16</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <CardMedia
              component="img"
              image="/season16-welcome.png"
              alt="RHWB Season 16 runners at the finish line"
              sx={{ 
                borderRadius: 1,
                width: '100%',
                height: 'auto',
                maxWidth: '100%',
                maxHeight: { xs: '300px', sm: '400px', md: '500px', lg: '600px' },
                objectFit: 'contain',
                objectPosition: 'center',
                display: 'block',
                margin: '0 auto',
                padding: { xs: '8px', sm: '12px', md: '16px' }
              }}
            />
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              Are you ready to kick off Season 16?!!
            </Typography>
            <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-line' }}>
              <strong>Our Program Offerings</strong><br/>
              RHWB now offers a{' '}
              <a 
                href="https://www.rhwb.org/training-programs" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                variety of programs
              </a>
              {' '}to suit diverse needs of our runners. Please review them before proceeding.
              <br/>
              <br/>
              Our program runs in a single track this season:
              <Box component="ul" sx={{ pl: 4, my: 0.5, mx: 0 }}>
                <Box component="li" sx={{ pl: 0.5 }}>
                  Summer (July-Nov)
                </Box>
              </Box>
              Review{' '}
              <a 
                href="https://www.rhwb.org/season-16-calendar" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Training Calendar
              </a>
              {' '}before deciding.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion 
        expanded={expanded === 'orientation'} 
        onChange={handleAccordionChange('orientation')}
        sx={{ mb: 3 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="orientation-content"
          id="orientation-header"
        >
          <Typography variant="h6">RHWB Orientation</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ position: 'relative', paddingTop: '56.25%', width: '100%' }}>
              <iframe
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px'
                }}
                src="https://www.youtube.com/embed/7Cl6v_VQgQI"
                title="RHWB Orientation Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen={true}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                New Runners:
              </Typography>
              <Typography variant="body1">
              It is mandatory that you watch the above orientation program before you jump in. We want you to first understand the program requirements and key details to make your journey with us successful.
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 2 }}>
                Return Runners:
              </Typography>
              <Typography variant="body1">
              We recommend you take some time to refresh key details and requirements for your continued success at RHWB.
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion 
        expanded={expanded === 'program-selection'} 
        onChange={handleAccordionChange('program-selection')}
        sx={{ mb: 3 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="program-selection-content"
          id="program-selection-header"
        >
          <Typography variant="h6">Program Selection for S16 Season (July-Nov)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Activity Type</InputLabel>
                <Select
                  name="activityType"
                  value={formData.activityType}
                  label="Activity Type"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="Running">Running</MenuItem>
                  <MenuItem value="Walking">Walking</MenuItem>
                  <MenuItem value="Masters">Masters (65 years or older)</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Segment</InputLabel>
                <Select
                  name="segment"
                  value={formData.segment}
                  label="Segment"
                  onChange={handleSelectChange}
                  disabled={formData.activityType === "Masters"}
                >
                  <MenuItem value="Coach Guided">Coach Guided</MenuItem>
                  <MenuItem value="Self Serve">Self Serve</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  name="userType"
                  value={formData.userType}
                  label="User Type"
                  onChange={handleSelectChange}
                  disabled={formData.segment === "Self Serve"}
                >
                  {formData.segment !== "Self Serve" && (
                    <MenuItem value="new">New Runners to RHWB</MenuItem>
                  )}
                  <MenuItem value="return">Returning Runners to RHWB</MenuItem>
                </Select>
              </FormControl>

              {formData.userType === 'return' && (
                <>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email used in previous season"
                    type="email"
                    value={formData.email}
                    onChange={handleTextChange}
                    required
                    error={!!emailError}
                    helperText={emailError || "Please enter the email you used in your previous season"}
                  />
                  
                  {formData.segment !== "Self Serve" && (
                    <FormControl fullWidth>
                      <InputLabel>Coaching Preference</InputLabel>
                      <Select
                        name="coachPreference"
                        value={formData.coachPreference}
                        label="Coaching Preference"
                        onChange={handleSelectChange}
                        required
                      >
                        <MenuItem value="stay">Happy to stay with my current coach</MenuItem>
                        <MenuItem value="open">Open to trying a new coach</MenuItem>
                      </Select>
                      <FormHelperText>
                        Let us know your coaching preference for this season. While we can't guarantee placements, we'll consider your input.
                      </FormHelperText>
                    </FormControl>
                  )}
                </>
              )}

              {formData.userType === 'new' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={orientationConfirmed}
                      onChange={(e) => setOrientationConfirmed(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="I confirm that as a new runner to RHWB, I have watched and understood the full orientation video."
                />
              )}

              <FormControl fullWidth>
  <InputLabel>Training Intensity</InputLabel>
  <Select
    name="trainingMode"
    value={formData.trainingMode}
    label="Training Intensity"
    onChange={handleSelectChange}
    disabled={
      formData.activityType === "Masters" ||
      formData.activityType === "Walking" ||
      !formData.segment
    }
  >
    {getTrainingModeOptions().map((mode) => (
      <MenuItem key={mode} value={mode}>
        {mode === 'Std' ? 'Standard (<2 years of running experience)' :
         mode === 'Exp' ? 'Experienced (>2 years of running experience)' :
         'High Intensity Speed (>4 years of running experience)'}
      </MenuItem>
    ))}
  </Select>
</FormControl>
              
              {formData.activityType && formData.segment && formData.userType && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 'bold',
                    }}
                  >
                    Your Program Cost: ${calculateProgramCost()}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mt: 0.5,
                    }}
                  >
                    (Price includes season training program + Running T-shirt)
                  </Typography>
                </Box>
              )}

              {formData.activityType && formData.segment && formData.userType && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    textAlign: 'center',
                    mt: 0
                  }}
                >
                  {formData.activityType === 'Masters' 
                    ? 'Use coupon code BALAFREE in the last step to waive the fees off'
                    : 'India & China residents only use coupon code BALADISCOUNT for additional discount'}
                </Typography>
              )}

              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  !formData.activityType || 
                  !formData.segment || 
                  !formData.userType || 
                  !formData.trainingMode || 
                  (formData.userType === 'new' && !orientationConfirmed) ||
                  (formData.userType === 'return' &&
                    (!formData.email ||
                      (formData.segment !== 'Self Serve' && !formData.coachPreference)))
                }
                sx={{ mt: 2 }}
              >
                {isSubmitting ? 'Submitting...' : 'Start Registration'}
              </Button>
            </Box>
          </Paper>
        </AccordionDetails>
      </Accordion>

      {/* Add feedback messages */}
      <Snackbar 
        open={submitSuccess} 
        autoHideDuration={6000} 
        onClose={() => setSubmitSuccess(false)}
      >
        <Alert severity="success" onClose={() => setSubmitSuccess(false)}>
          Registration data saved successfully!
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!submitError} 
        autoHideDuration={6000} 
        onClose={() => setSubmitError(null)}
      >
        <Alert severity="error" onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProgramForm; 
